<template>
  <div>
    <b-row class="border-bottom py-3" v-if="showReadinessDate">
      <b-col cols="12" lg="3" class="text-muted">
        <template v-if="order.is_precheck">
          {{ $t('inputs.headings.precheck-readiness_date') }}:
        </template>
        <template v-else-if="order.is_activation">
          {{ $t('inputs.headings.activation-readiness_date') }}:
        </template>
        <template v-else>{{ $t('inputs.headings.readiness_date') }}:</template>
      </b-col>
      <b-col cols="12" lg="9">
        {{ order.job.readiness_date | moment('L') }}
      </b-col>
    </b-row>
    <b-row class="border-bottom py-3" v-if="showDeliveryDate">
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('inputs.headings.delivery_date') }}:
      </b-col>
      <b-col cols="12" lg="9">
        <template
          v-if="
            order.opportunity.email_type == 'delivery_to_partner_with_assembly'
          "
        >
          {{ $t('no-information') }}
        </template>
        <template v-else>{{ order.job.date_expected | moment('L') }}</template>
      </b-col>
    </b-row>
    <template v-if="!short">
      <b-row class="border-bottom py-3" v-if="availabilityDates.length > 0">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('inputs.headings.availability_dates') }}:
        </b-col>
        <b-col cols="12" lg="9">
          <ul class="mb-0">
            <li v-for="(date, index) in availabilityDates" :key="index">
              <strong>{{ date.start | moment(' dd, L') }},</strong>
              <span>{{ date.start | moment('H:mm') }}</span>
              -
              <strong v-if="isMultidayPeriod(date.start, date.end)">
                {{ date.end | moment(' dd, L') }},
              </strong>
              <span>{{ date.end | moment('H:mm') }}</span>
            </li>
          </ul>
        </b-col>
      </b-row>
      <b-row class="border-bottom py-3" v-if="displayDateTimeSection">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('date-and-time') }}:
        </b-col>
        <b-col cols="12" lg="9">
          <template v-if="!confirmedAppointment">
            <i18n
              path="order-details.appointment-prompt.text"
              tag="p"
              class="mb-0"
            >
              <a
                slot="more_link"
                href="#order-actions"
                @click.prevent="focusOnActions"
              >
                {{ $t('order-details.appointment-prompt.link-label') }}
              </a>
            </i18n>
            <template v-if="type === 'open'">
              <button
                class="btn-unreachable"
                v-b-modal:[`customer-unreachable-${order.sfid}`]
              >
                {{ $t('customer-unreachable') }}?
              </button>
            </template>
          </template>
          <template v-else-if="order.job.appointment_start_timestamp">
            <strong>
              {{ order.job.appointment_start_timestamp | moment('L') }}
            </strong>
            ,
            {{ order.job.appointment_start_timestamp | moment('H:mm') }} -
            <strong
              v-if="
                isMultidayPeriod(
                  order.job.appointment_start_timestamp,
                  order.job.appointment_end_timestamp,
                )
              "
            >
              {{ order.job.appointment_end_timestamp | moment('L') }},
            </strong>
            {{ order.job.appointment_end_timestamp | moment('H:mm') }}
          </template>
        </b-col>
      </b-row>
      <!--
        Taking out install time for now until data has been fixed.
        <b-row class="border-bottom py-3">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('install-time') }}:
        </b-col>
        <b-col cols="12" lg="9">{{ serviceTotalTime }}</b-col>
      </b-row> -->
    </template>
    <slot name="actions" />
    <b-row class="border-bottom py-3" v-if="onHoldReasons.length > 0">
      <b-col cols="12" lg="3" class="text-muted mb-2">
        {{ $t('on-hold-reasons') }}:
      </b-col>
      <b-col cols="12" lg="9">
        <div
          v-for="(reason, index) in filteredOnHoldReasons"
          :key="`reason-${index}`"
        >
          <b-badge variant="warning" class="gid-onhold-badge">
            {{ $t(`state.${reason.state}`) }}
          </b-badge>
          <template v-if="reason.event">
            -&gt;
            <b-button
              size="sm"
              variant="primary"
              @click="$emit('sendStateEvent', reason.event)"
            >
              {{ $t(`state-events.${reason.event}`) }}
            </b-button>
          </template>
          <template v-else-if="reason.custom">
            -&gt;
            <b-button
              size="sm"
              variant="primary"
              @click="reason.custom.action"
              :disabled="loadingAction"
            >
              <b-spinner v-if="loadingAction" small></b-spinner>
              {{ reason.custom.label }}
            </b-button>
          </template>
          <div v-if="reason.details" class="small text-muted mt-1">
            {{ reason.details }}
          </div>
        </div>
        <div
          v-if="productDeliveryReason"
          class="d-flex flex-row align-items-center my-3"
          style="gap: 30px"
        >
          <b-badge variant="warning" class="gid-onhold-badge">
            {{ $t(`state.${productDeliveryReason.state}`) }}
          </b-badge>
          <div class="d-flex flex-column" style="gap: 30px">
            <div
              class="d-flex flex-column align-items-start"
              v-for="(order, index) in productDeliveryReason.orders"
              :key="index"
            >
              <strong
                v-for="product in order.items"
                :key="product.id"
                class="gid-product-name"
                :title="product.name"
              >
                {{ product.quantity }} x {{ product.name }}
              </strong>
              <b-button
                size="sm"
                variant="primary"
                @click="$emit('sendProductDelivery', order)"
              >
                {{ $t(`confirm-receipt`) }}
              </b-button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="canDelegate(order)" class="border-bottom py-3">
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('delegate.order-details-title') }}:
      </b-col>
      <b-col cols="12" lg="9">
        <b-button variant="outline-primary" @click="$emit('delegate')">
          <BIconDiagram3Fill class="mx-2" />
          {{ $t('delegate.order-details') }}
        </b-button>
        <b-dropdown variant="outline-primary" class="ml-2">
          <template #button-content>
            <BIconDownload class="mx-2" />
            {{ $t('order-details.gaeb-export') }}
          </template>
          <b-dropdown-item @click="gaebExportJob('x84')">
            {{ $t('gaeb.x84') }}
          </b-dropdown-item>
          <b-dropdown-item @click="gaebExportJob('x83')">
            {{ $t('gaeb.x83') }}
          </b-dropdown-item>
          <b-dropdown-item @click="gaebExportJob('x81')">
            {{ $t('gaeb.x81') }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
    </b-row>
    <b-row
      v-if="order.delegated_to && order.delegated_to.email"
      class="border-bottom py-3"
    >
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('orders-list.delegated-to') }}:
      </b-col>
      <b-col cols="12" lg="9">
        {{ order.delegated_to.first_name }} {{ order.delegated_to.last_name }}
      </b-col>
    </b-row>
    <b-row class="border-bottom py-3">
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('partner-order-eid') }}:
      </b-col>
      <b-col cols="3">
        <OrderEid :order="order" :allowPartnerEidEdit="false" />
      </b-col>
    </b-row>
    <b-row class="border-bottom py-3" v-if="order.job.invoicing_eid">
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('invoicing_eid') }}:
      </b-col>
      <b-col cols="12" lg="9">{{ order.job.invoicing_eid }}</b-col>
    </b-row>
    <b-row class="border-bottom py-3">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('project-id') }}:</b-col>
      <b-col cols="12" lg="9">
        {{ order.project.name }}
      </b-col>
    </b-row>
    <b-row class="border-bottom py-3" v-if="order.job.eid">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('eid') }}:</b-col>
      <b-col cols="12" lg="9">{{ order.job.eid }}</b-col>
    </b-row>
    <b-row class="border-bottom py-3">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('address') }}:</b-col>
      <b-col cols="12" lg="9">
        <a
          :href="`https://maps.google.com/?q=${order.customer.shipping_address.street}+${order.customer.shipping_address.city}+${order.customer.shipping_address.postal_code}`"
          target="_blank"
        >
          {{ order.customer.name }},
          <br />
          <template v-if="order.customer.company_name">
            {{ order.customer.company_name }},
            <br />
          </template>
          {{ order.customer.shipping_address.street }},
          <br />
          {{ order.customer.shipping_address.postal_code }}
          {{ order.customer.shipping_address.city }}
        </a>
      </b-col>
    </b-row>
    <b-row
      class="border-bottom py-3"
      v-if="order.job_contact_person && order.job_contact_person.first_name"
    >
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('contact-person.section-title') }}:
      </b-col>
      <b-col cols="12" lg="9">
        <div class="mb-2" v-if="contactPersonNameDifferent">
          <strong>{{ $t('contact-person.name-label') }}:</strong>
          {{ order.job_contact_person.first_name }}
          {{ order.job_contact_person.last_name }}
        </div>
        <div class="mb-2" v-if="phones.length > 0">
          <strong>{{ $t('contact-person.phone-label') }}:</strong>
          <span v-for="(phone, index) in phones" :key="index">
            <a :href="`tel:${phone}`" @click="contactCustomer">{{ phone }}</a>
            <span v-if="index != phones.length - 1">,</span>
          </span>
        </div>
        <div v-if="customerEmail">
          <strong>{{ $t('contact-person.email-label') }}:</strong>
          <a
            :href="`mailto:${customerEmail}?cc=partner@getitdone.rocks&subject=${$t(
              'contact-person.mail-subject',
            )}%20${order?.job?.name}`"
          >
            {{ customerEmail }}
          </a>
        </div>
      </b-col>
    </b-row>
    <template v-else>
      <b-row class="border-bottom py-3">
        <b-col cols="12" lg="3" class="text-muted">{{ $t('phone') }}:</b-col>
        <b-col cols="12" lg="9">
          <template v-if="phones.length > 0">
            <span v-for="(phone, index) in phones" :key="index">
              <a :href="`tel:${phone}`" @click="contactCustomer">{{ phone }}</a>
              <span v-if="index != phones.length - 1">,</span>
            </span>
          </template>
          <span v-else>{{ $t('none') }}</span>
        </b-col>
      </b-row>
      <b-row class="border-bottom py-3">
        <b-col cols="12" lg="3" class="text-muted">{{ $t('e-mail') }}:</b-col>
        <b-col cols="12" lg="9">
          <a
            v-if="order.customer.email"
            :href="`mailto:${customerEmail}?cc=partner@getitdone.rocks&subject=${$t(
              'contact-person.mail-subject',
            )}%20${order?.job?.name}`"
          >
            {{ customerEmail }}
          </a>
          <span v-else>{{ $t('none') }}</span>
        </b-col>
      </b-row>
    </template>
    <b-row
      class="border-bottom py-3"
      v-if="technicalSupportContacts.length > 0"
    >
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('technical-support') }}:
      </b-col>
      <b-col cols="12" lg="9">
        <b-button
          variant="outline-primary"
          size="sm"
          @click="showTechnicalSupportModal = true"
        >
          <span class="material-icons align-middle mr-2">phone</span>
          {{ $t('contact-support') }}
        </b-button>
      </b-col>
    </b-row>
    <diary :order="order" />
    <b-row class="border-bottom py-3">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('remark') }}:</b-col>
      <b-col cols="12" lg="9">
        <comments
          :job-api-id="order.api_id"
          :disable-new-comments="shouldDisableComments"
        />
      </b-col>
    </b-row>
    <b-row
      class="border-bottom py-3"
      v-if="Array.isArray(formAnswers) && formAnswers.length > 0"
    >
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('inputs.headings.form_answers') }}:
      </b-col>
      <b-col cols="12" lg="9">
        <form-answers
          :answers="formAnswers"
          :open="false"
          :complete-order="order"
        />
      </b-col>
    </b-row>
    <b-row class="border-bottom py-3" v-if="order.job.professional_comment">
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('professional_comment_customer') }}:
      </b-col>
      <b-col cols="12" lg="9">
        <textarea
          :value="order.job.professional_comment"
          readonly
          wrap="soft"
          rows="8"
          max-rows="12"
          class="form-control"
        ></textarea>
      </b-col>
    </b-row>
    <b-row class="border-bottom py-3">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('tasks') }}:</b-col>
      <b-col cols="12" lg="9">
        <services-preview
          :services="order.services"
          :current-price-purchase="pricePurchasingForPartner"
          :use-price-purchase="true"
          :show-price-purchase="!hidePricePurchasing"
          :show-value-entry="true"
          :opportunity-id="order.opportunity.sfid"
          :custom_services="order.custom_services"
        />
      </b-col>
    </b-row>

    <b-row class="border-bottom py-3" v-if="order.job.eid">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('eid') }}:</b-col>
      <b-col cols="12" lg="9">{{ order.job.eid }}</b-col>
    </b-row>

    <b-row class="border-bottom py-3" v-if="order.job.invoicing_eid">
      <b-col cols="12" lg="3" class="text-muted">
        {{ $t('invoicing_eid') }}:
      </b-col>
      <b-col cols="12" lg="9">{{ order.job.invoicing_eid }}</b-col>
    </b-row>

    <b-row class="border-bottom py-3" v-if="order.products.length">
      <b-col cols="12" lg="3" class="text-muted">{{ $t('products') }}:</b-col>
      <b-col cols="12" lg="9">
        <div
          :class="order.products.length > 1 ? 'border-bottom pb-3' : ''"
          v-for="(product, index) in compiledOrder.products"
          :key="index"
        >
          <span>
            <strong>{{ product.quantity }} x {{ product.name }}</strong>
            <b-badge
              :variant="
                product.supplier === 'partner' ? 'warning' : 'secondary'
              "
              class="ml-2"
            >
              {{
                product.supplier === 'partner'
                  ? $t('products-order.needs-to-be-ordered')
                  : `${$t('products-order.will-be-ordered')}` +
                    ' ' +
                    $t(`products-order.${product.supplier}`)
              }}
            </b-badge>
          </span>
          <div v-if="validateUrl(product.url)">
            <a :href="validateUrl(product.url)" target="_blank">
              {{ $t('order-details.products.url') }}
            </a>
          </div>
          <div
            v-if="formattedMultiValue(product.serial_number)"
            class="text-muted small"
          >
            SN: {{ formattedMultiValue(product.serial_number) }}
          </div>
          <div
            v-if="formattedMultiValue(product.chargepoint_number)"
            class="text-muted"
          >
            Chargepoint: {{ formattedMultiValue(product.chargepoint_number) }}
          </div>
          <div
            v-if="formattedMultiValue(product.product_password)"
            class="text-muted"
          >
            Password: {{ formattedMultiValue(product.product_password) }}
          </div>
          <div
            v-if="product.hint || productFilesForProduct(product.id).length > 0"
            class="gid-product-hints bg-light small text-muted border mt-2 rounded p-2"
          >
            <div class="flex-grow-1">
              <div v-html="product.hint"></div>
            </div>
            <div
              class="gid-product-files bg-light mt-n1 pt-2 pb-2"
              v-if="productFilesForProduct(product.id).length > 0"
            >
              <div class="small text-muted mb-2">
                <strong>{{ $t('inputs.products.product-files') }}:</strong>
              </div>
              <div class="d-flex flex-wrap">
                <div
                  v-for="file in productFilesForProduct(product.id)"
                  :key="file.id"
                  class="product-file-item mr-2 mb-2"
                >
                  <a
                    :href="file.image.path"
                    target="_blank"
                    class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    :title="$t(`inputs.products.file-types.${file.type}`)"
                  >
                    <span class="small">
                      {{
                        file?.image?.labelText
                          ? file.image.labelText
                          : $t(`inputs.products.file-types.${file.type}`)
                      }}
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div v-if="checkProductDetails">
            <b-icon-box class="mr-2" />
            {{ $t('products-order.tracking-link') }}:
            <a :href="getProductOrderInfo(product.id, 'url')" target="_blank">
              {{ getProductOrderInfo(product.id, 'trackingID') }}
            </a>
          </div>
          <div v-if="checkProductDetails">
            {{ $t('products-order.shipping-to') }}:
            <b-badge variant="secondary">
              {{
                $t(
                  `products-order.${getProductOrderInfo(product.id, 'shipTo')}`,
                )
              }}
            </b-badge>
          </div>
        </div>
      </b-col>
    </b-row>
    <products-list-info
      :context="'orders'"
      :orderSfid="order.sfid"
      :products="order.products"
    />
    <ProductFilesSection
      v-if="order.opportunity"
      :opportunity-sfid="order.opportunity.sfid"
      :is-precheck="order.is_precheck"
      :collapsible="true"
      :default-collapsed="true"
      :brand="order.brand"
      context="partner"
    />

    <b-row
      v-if="formLoadError"
      key="form-load-error"
      class="border-bottom py-3"
    >
      <b-col cols="12" class="text-center py-3 form-load-error">
        <i class="material-icons form-load-error__icon">cloud_off</i>
        <h6 class="mt-2 mb-1">{{ $t('form-load-error.title') }}</h6>
        <p class="text-muted small mb-1">
          {{ $t('form-load-error.description') }}
        </p>
        <p class="text-muted small mb-3">
          {{ $t('form-load-error.instruction') }}
        </p>
        <b-button
          variant="primary"
          size="sm"
          :disabled="formLoadRetrying"
          @click="retryFormLoad"
        >
          <b-spinner v-if="formLoadRetrying" small class="mr-2" />
          {{
            formLoadRetrying
              ? $t('form-load-error.retrying')
              : $t('form-load-error.retry')
          }}
        </b-button>
      </b-col>
    </b-row>

    <template v-for="document of allDocuments">
      <b-row
        v-if="
          document.files.length ||
          (customJmfFiles.length && document.resource === 'available-info')
        "
        :key="document.resource"
        class="border-bottom py-3"
      >
        <b-col cols="12" lg="3" class="text-muted mb-2">
          {{ $t(`order-details.${document.resource}`) }}:
        </b-col>
        <b-col cols="12" lg="9">
          <span
            v-for="(file, index) in document.files"
            :key="index"
            style="width: fit-content"
          >
            <a
              :id="`protocol-template-${file.name}`"
              :class="`btn btn-sm px-3 mr-2 mb-2 rounded-pill ${shouldBeDisabled} ${badgeColor(
                file,
              )} ${badgeColor(file)}`"
              :href="buildProtocolLink(file)"
              target="_blank"
              @click="file.tracking ? gaTrackPdfDownload : null"
            >
              <i class="material-icons align-middle size-lg">
                <template v-if="file.type == 'pdf'">picture_as_pdf</template>
                <template v-else>image</template>
              </i>
              {{
                file.translateName
                  ? $t(getProtocolTranslationKey(file))
                  : file.name
              }}
              {{ index > 0 ? `[${index}]` : null }}
              <b-tooltip
                v-if="file.resource == 'protocol' && file.tracking"
                triggers="hover"
                :target="`protocol-template-${file.name}`"
              >
                {{ $t('protocol-template.tooltip') }}
              </b-tooltip>
            </a>
          </span>
          <template v-if="document.resource === 'available-info'">
            <span
              v-for="(file, index) in customJmfFiles"
              :key="`jmf-${index}`"
              style="width: fit-content"
            >
              <a
                :class="`btn btn-sm px-3 mr-2 mb-2 rounded-pill ${shouldBeDisabled} ${badgeColor(
                  file,
                )}`"
                :href="`/api/media/file/${file.path}?jwt=${accessToken}`"
                target="_blank"
              >
                <i class="material-icons align-middle size-lg">
                  <template v-if="file.type === 'pdf'">picture_as_pdf</template>
                  <template v-else>image</template>
                </i>
                {{ file.name }}
                {{ `[${file.index}]` }}
              </a>
            </span>
          </template>
        </b-col>
      </b-row>
    </template>

    <b-row class="border-bottom py-3" v-if="shouldDisplayActivationNotice">
      <b-col cols="12" lg="9">
        <p><strong>Wichtiger Hinweis</strong></p>
        <p>
          Bis auf Weiteres muss vor der Inbetriebnahme der BayWa Wallboxen eine
          Einstellung von uns (GID) vorgenommen werden, die erst passieren kann,
          sobald die Wallbox am Strom hängt. Da vor dieser Einstellung die
          finale Inbetriebnahme von Ihnen nicht abgeschlossen werden kann,
          bitten wir unbedingt um eine Kontaktaufnahme, wenn die Wallbox am
          Strom hängt.
        </p>
      </b-col>
    </b-row>
    <CustomerUnreachableModal :order="order" :isOrderDetails="true" />
    <CallContactModal
      :visible="showTechnicalSupportModal"
      :contactList="technicalSupportContacts"
      @hide="showTechnicalSupportModal = false"
      @call-initiated="handleTechnicalSupportCall"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

import ServicesPreview from '@gid/vue-common/components/ServicesPreview.vue';
import ProductsListInfo from '@/components/ProductsListInfo.vue';
import FormAnswers from '@gid/vue-common/components/inputs/FormAnswers.vue';
import Comments from '@gid/vue-common/components/Comments';
import {
  JobStatusEnum,
  UserRoleEnum,
  JobMediaFileTypeEnum,
  ProductConnectionType,
} from '@gid/models';
import {
  BIconBox,
  BIcon,
  BIconDiagram3Fill,
  BIconDownload,
} from 'bootstrap-vue';
import CanDelegateOrder from './CanDelegateOrderMixin';
import networkRetryMixin from '@/mixins/networkRetryMixin';
import { StatusRulesNamesEnum } from '@gid/models/dist/entities/status-rules-names-enum';
import { BAYWA_DIENSTWAGENFAHRER_OPP } from './Order.vue';
import { CommentActionTypeEnum } from '@gid/models/dist/entities/comment';
import CustomerUnreachableModal from './CustomerUnreachableModal.vue';
import OrderEid from '@/components/orders/OrderEidAddEdit';
import Diary from '@/components/orders/Diary';
import TemplateMixin from '@gid/vue-common/components/mixins/templateMixin';
import CallContactModal from '@gid/vue-common/components/CallContactModal';
import ProductFilesSection from '@gid/vue-common/components/ProductFilesSection.vue';
import { parseMultiValueField } from '@gid/vue-common/utils';

// The network-resilient form fetches whose failure surfaces the shared
// "connectivity problem" banner in the at-work view (GID-2575).
const FORM_LOAD_KEYS = ['protocols', 'formAnswers', 'partnerBriefing'];

const stateToEventMap = {
  [`${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.PARTNER_QUOTE_VALIDATION}.pending-validation`]: `PARTNER_QUOTE_VALIDATED`,
  [`${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.CUSTOMER_PREWORK_BEFORE_ACCEPTED}.waiting`]: `CUSTOMER_PREWORK_BEFORE_ACCEPTED_DONE`,
  // GID-2485: let the partner resolve the "waiting for network operator
  // response" on-hold reason (jobs-service now allows ADMIN + PARTNER).
  [`${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.NETWORK_OPERATOR}.waiting`]: `NETWORK_OPERATOR_RESPONDED`,
  [`${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CUSTOMER_NOT_REACHABLE}.unreachable`]: `CUSTOMER_REACHED`,
  [`${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CUSTOMER_PREWORK}.awaiting`]: `CUSTOMER_PREWORK_DONE`,
  [`${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PARTNER_INFO}.awaiting`]: `PARTNER_INFO_PROVIDED`,
  [`${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PARTNER_PAUSE_GENERIC}.paused`]: `PARTNER_UNPAUSE`,
  [`${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.BRAND_PRODUCT_ORDERING}.waiting`]: `BRAND_PRODUCT_ORDERING_DONE`,
};
export default {
  components: {
    Comments,
    Diary,
    ServicesPreview,
    ProductsListInfo,
    FormAnswers,
    BIconBox,
    BIconDiagram3Fill,
    BIconDownload,
    CustomerUnreachableModal,
    OrderEid,
    CallContactModal,
    ProductFilesSection,
  },
  mixins: [CanDelegateOrder, TemplateMixin, networkRetryMixin],
  props: {
    order: Object,
    actionOptions: Object,
    short: Boolean,
    hidePricePurchasing: Boolean,
    productsOrderDetails: Array,
  },
  data() {
    return {
      JobMediaFileTypeEnum,
      formAnswers: [],
      protocols: {
        orderDetails: [],
        protocol: [],
      },
      loadingAction: false,
      partnerBriefingDocuments: [],
      type: this.$route.params.type,
      jmfPicklist: [],
      showTechnicalSupportModal: false,
    };
  },
  computed: {
    ...mapGetters(['user', 'contactProfiles']),
    shouldBeDisabled() {
      return this.order.async_appointment && 'disabled';
    },
    accessToken() {
      return this.$store.state.auth.access_token;
    },
    badgeColor() {
      return (file) => {
        if (file.resource === 'partner-briefing' && file.updatedAt) {
          const oneMonthAgo = this.$moment().subtract(1, 'month');

          const fileDate = this.$moment(file.updatedAt);
          if (fileDate.isSameOrAfter(oneMonthAgo)) {
            return 'btn-warning pl-3 pr-5 new-pdf-available up-down';
          }
        }

        return file.resource === JobMediaFileTypeEnum.CUSTOMER_BLUEPRINT
          ? 'btn-info'
          : 'btn-primary';
      };
    },
    isEnercityWallbox() {
      return ['0060800000wd8lbAAA'].includes(this.order.opportunity.sfid);
    },
    isBaywaDienstwagenfahrerOpp() {
      return this.order?.opportunity.sfid === BAYWA_DIENSTWAGENFAHRER_OPP;
    },
    serviceTotalTime() {
      const duration = this.$moment.duration(
        this.order.services_total_minutes,
        'minutes',
      );

      let result = [];
      if (duration.days()) {
        result.push(
          this.$moment.duration(duration.days(), 'days').format('d __'),
        );
      }
      if (duration.hours()) {
        result.push(
          this.$moment.duration(duration.hours(), 'hours').format('h __'),
        );
      }
      if (duration.minutes()) {
        result.push(
          this.$moment.duration(duration.minutes(), 'minutes').format('m __'),
        );
      }

      return result.join(' ');
    },
    shouldDisplayActivationNotice() {
      return (
        [
          JobStatusEnum.ACCEPTED,
          JobStatusEnum.APPOINTMENT_CONFIRMED,
          JobStatusEnum.STARTED,
          JobStatusEnum.ON_HOLD,
          JobStatusEnum.PROPOSAL_APPROVAL_PENDING,
          JobStatusEnum.BRAND_PROPOSAL_APPROVAL_PENDING,
        ].includes(this.order?.job?.status) &&
        this.order?.is_installation &&
        this.isBaywaDienstwagenfahrerOpp
      );
    },
    shouldDisableComments() {
      return [
        JobStatusEnum.CANCELLED,
        JobStatusEnum.INVOICE_SENT,
        JobStatusEnum.CLOSED,
      ].includes(this.order?.job?.status);
    },
    customerEmail() {
      return this.order.job_contact_person.email
        ? `${this.order.job_contact_person.email}`
        : this.order.customer.email;
    },
    contactPersonNameDifferent() {
      const contactName = `${this.order.job_contact_person.first_name || ''} ${
        this.order.job_contact_person.last_name || ''
      }`.trim();
      const customerName = this.order.customer.name || '';
      return (
        contactName && contactName.toLowerCase() !== customerName.toLowerCase()
      );
    },
    phones() {
      if (this.order.job_contact_person?.phone) {
        return [this.order.job_contact_person.phone];
      }
      let phones = [];
      ['phone', 'mobile_phone'].forEach((phone) => {
        if (this.order.customer[phone]) phones.push(this.order.customer[phone]);
      });
      return phones;
    },
    technicalSupportContacts() {
      const contacts =
        this.order?.opportunity?.technical_support_contacts || [];
      // Transform contacts to support multiple phone numbers like admin system
      return contacts
        .map((contact, index) => {
          return {
            id: contact.sfid || index,
            firstName: contact?.first_name || '',
            lastName: contact?.last_name || '-',
            phone: contact.phone || contact.mobile_phone,
            mobilePhone: contact.mobile_phone || contact.phone,
          };
        })
        .filter((contact) => contact.phone || contact.mobilePhone);
    },
    needsDates() {
      return (
        this.actionOptions &&
        this.actionOptions.statuses &&
        this.actionOptions.statuses.some(
          (status) => status.name == JobStatusEnum.APPOINTMENT_CONFIRMED,
        )
      );
    },
    confirmedAppointment() {
      return (
        this.order.job.appointment_start_timestamp &&
        [
          JobStatusEnum.APPOINTMENT_CONFIRMED,
          JobStatusEnum.COMPLETED,
          JobStatusEnum.INVOICE_SENT,
          JobStatusEnum.STARTED,
          JobStatusEnum.FINISHED,
          JobStatusEnum.APPROVAL_PENDING,
        ].includes(this.order.job.status)
      );
    },
    showDeliveryDate() {
      if (this.order.job.date_expected) {
        const deliveryDate = this.$moment(this.order.job.date_expected).startOf(
          'day',
        );
        const createdDate = this.$moment(this.order.job.created_date).startOf(
          'day',
        );
        return deliveryDate.isAfter(createdDate);
      }
      return false;
    },
    showReadinessDate() {
      return !!this.order.job.readiness_date;
    },
    availabilityDates() {
      if (this.needsDates) {
        return this.order.job.dates.filter((date) => {
          const start = this.$moment(date.start).startOf('day');
          const nowDate = this.$moment().startOf('day');
          const deliveryDate = this.order.job.date_expected
            ? this.$moment(this.order.job.date_expected).startOf('day')
            : this.$moment(this.order.job.date_created).startOf('day');
          return start.isAfter(nowDate) && start.isAfter(deliveryDate);
        });
      }
      return [];
    },
    displayDateTimeSection() {
      return (
        !this.order.async_appointment &&
        (this.needsDates || this.order.job.appointment_start_timestamp)
      );
    },
    checkProductDetails() {
      return this.productsOrderDetails?.length;
    },
    pricePurchasingForPartner() {
      return this.order.job.service_price_purchasing_sum;
    },
    onHoldReasons() {
      const stateDetailsRoot = this.order.job.states?.details;
      const resolveDetails = (state) => {
        const keys = state.split('.').slice(0, -1);
        let node = stateDetailsRoot;
        for (const key of keys) {
          node = node?.[key];
        }
        return node?.details;
      };
      return (
        this.order.job.states?.list?.[0]?.startsWith(this.order.job.status) &&
        this.order.job.states?.list?.[0].includes('.')
          ? this.order.job.states?.list
          : []
      ).map((state) => {
        const event = stateToEventMap[state];
        const details = resolveDetails(state);
        const base = this.actionOptions.jobServiceEvents.includes(event)
          ? { state, event }
          : { state };
        return details ? { ...base, details } : base;
      });
    },
    filteredOnHoldReasons() {
      const hiddenStates = [
        `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PRODUCT_ORDERING}.pending-delivery`,
      ];
      return this.onHoldReasons
        .filter(({ state }) => !hiddenStates.includes(state))
        .map((reason) => {
          if (
            reason.state ===
            `${JobStatusEnum.STARTED}.${StatusRulesNamesEnum.CUSTOMER_SIGNATURE}.missing-customer`
          ) {
            if (
              !this.user?.account.restrict_protocol_submission ||
              this.contactProfiles.some((r) =>
                ['admin', 'company owner'].includes(r),
              )
            ) {
              reason.custom = {
                action: this.sendAbsentCustomerReminder,
                label: this.$t('state-actions.prompt-customer-signature'),
              };
            }
          }
          return reason;
        });
    },
    productDeliveryReason() {
      const pdReason = this.onHoldReasons.find(
        ({ state }) =>
          state ===
          `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PRODUCT_ORDERING}.pending-delivery`,
      );
      if (pdReason) {
        const productsNoTrackingId = this.productsOrderDetails
          .filter(({ trackingID }) => !trackingID)
          .map((productOrder) => ({
            ...productOrder,
            items: productOrder.items.map(({ id }) =>
              this.order.products.find((product) => product.id === id),
            ),
          }));
        return { ...pdReason, orders: productsNoTrackingId };
      }
      return null;
    },
    // True while any form fetch is stuck on a network problem (GID-2575).
    // Stays true through the loading flicker of a retry; clears on success.
    formLoadError() {
      return FORM_LOAD_KEYS.some((key) => {
        const state = this.netRetry[key];
        return state && state.hadError && state.status !== 'loaded';
      });
    },
    // A retry (auto-backoff or manual) is currently in flight.
    formLoadRetrying() {
      return FORM_LOAD_KEYS.some((key) => {
        const state = this.netRetry[key];
        return (
          state &&
          state.hadError &&
          (state.retrying || state.status === 'loading')
        );
      });
    },
    allDocuments() {
      return [
        {
          resource: 'available-info',
          files: this.protocols.orderDetails,
        },
        {
          resource: 'partner-briefing',
          files: this.partnerBriefingDocuments.filter(
            (pbd) => pbd.resource === 'partner-briefing',
          ),
        },
        {
          resource: 'additional-documents',
          files: this.partnerBriefingDocuments.filter(
            (pbd) => pbd.resource === 'additional-documents',
          ),
        },
        {
          resource: 'protocol',
          files: this.protocols.protocol,
        },
      ];
    },
    customJmfFiles() {
      const jmfFiles = [];
      if (!this.order.files) return jmfFiles;

      Object.values(this.order.files).forEach(({ paths, type }) => {
        // Skip files with type 'brand_quote' or 'customer_quote'
        if (type === 'brand_quote' || type === 'customer_quote') return;
        const filteredPicklistValue = this.jmfPicklist.find(
          (item) => item.valueName === type,
        );

        paths.forEach((path, index) => {
          jmfFiles.push({
            path,
            type,
            name: filteredPicklistValue?.label || type.replace(/_/g, ' '),
            resource: type,
            index,
          });
        });
      });

      return jmfFiles;
    },
    compiledOrder() {
      return this.compileHintTemplate(this.order);
    },
    productFilesForProduct() {
      return (productId) => {
        const product = this.order?.products?.find((p) => p.id === productId);
        if (!product) return [];

        const normalFiles = product.productFiles || [];
        const opportunityFiles = product.productOpportunityFiles || [];

        const filesByType = {};
        const resultFiles = [];

        normalFiles.forEach((file) => {
          const fileType = file.type;
          if (!filesByType[fileType]) {
            filesByType[fileType] = { normal: null, opportunity: null };
          }
          filesByType[fileType].normal = file;
        });

        opportunityFiles.forEach((file) => {
          const fileType = file.type;
          if (!filesByType[fileType]) {
            filesByType[fileType] = { normal: null, opportunity: null };
          }

          if (file.connection_type === ProductConnectionType.ADDITIONAL) {
            filesByType[fileType].opportunity = file;
          } else if (
            file.connection_type === ProductConnectionType.ALTERNATIVE
          ) {
            filesByType[fileType].opportunity = file;
            filesByType[fileType].normal = null;
          }
        });

        // Build final result
        Object.values(filesByType).forEach(({ normal, opportunity }) => {
          if (normal) resultFiles.push(normal);
          if (opportunity) resultFiles.push(opportunity);
        });

        return resultFiles;
      };
    },
  },
  methods: {
    gaebExportJob(format = 'x84') {
      const jobApiId = this.order.job.api_id;
      const url = `/data-api/gaeb-export/job/${jobApiId}?priceTarget=partner&format=${format}&jwt=${this.accessToken}`;
      window.open(url, '_blank');
    },
    getProtocolTranslationKey(file) {
      if (file.resource === 'protocol' && file.tracking) {
        return file.name === 'protocol-customer'
          ? 'protocol-template.customer'
          : 'protocol-template.protocol';
      }
      return file.name;
    },
    async sendAbsentCustomerReminder() {
      this.loadingAction = true;
      try {
        await axios.post('/api/partner/send-email', {
          template_name: 'customer/missing_presence',
          job_id: this.order.job.sfid,
        });
        this.$bvToast.toast(this.$t('e-mail-sent'), {
          title: this.$t('successful'),
          variant: 'success',
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast(this.$t('_errors.general.title'), {
          title: this.$t('_errors.general.title'),
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loadingAction = false;
      }
    },
    getProductOrderInfo(productId, prop) {
      return this.productsOrderDetails?.find(({ items }) =>
        items?.some(({ id }) => id == productId),
      )?.[prop];
    },
    contactCustomer() {
      this.createComment(this.order.job.api_id, this.$t('call.customer'), [
        UserRoleEnum.ADMIN,
        UserRoleEnum.BRAND,
        UserRoleEnum.CUSTOMER,
        UserRoleEnum.PARTNER,
      ]);
    },
    contactSupport(contact) {
      this.createComment(
        this.order.job.api_id,
        this.$t('call.support-call', { phone: contact.phone }),
        [UserRoleEnum.ADMIN, UserRoleEnum.BRAND, UserRoleEnum.CUSTOMER],
      );
    },
    createComment(jobApiId, message, visibleFor) {
      const commentData = {
        actionType: CommentActionTypeEnum.PHONE,
        jobApiId,
        message,
        visibleForRoles: visibleFor,
      };
      axios.post(`/data-api/comment`, commentData);
    },
    focusOnActions() {
      this.$emit('scrollIntoView', { block: 'start', behavior: 'smooth' });
    },
    gaTrackPdfDownload() {
      this.$gtag.event('Single pdf download', {
        event_category: 'Opportunity',
        event_label: this.order.opportunity.name,
      });
    },
    isMultidayPeriod(start, end) {
      return !this.$moment(start).isSame(end, 'day');
    },
    buildProtocolLink(file) {
      return file.linkParams
        ? `${file.link}?${file.linkParams}`
        : `${file.link}`;
    },
    fetchFormAnswers() {
      // Wrapped in runWithRetry so a field-network timeout no longer leaves the
      // answers silently missing — it auto-retries with backoff and surfaces the
      // connectivity banner (GID-2575).
      return this.runWithRetry('formAnswers', () =>
        axios
          .get(`/order-intake-forms-api/form/answers/${this.order.api_id}`)
          .then((response) => {
            this.formAnswers = response.data;
          }),
      );
    },
    fetchProtocols() {
      // The protocol form/links live behind this single fetch; its previous
      // silent `.catch` is what made "partner can't see the protocol form"
      // invisible on a bad connection (GID-2575).
      return this.runWithRetry('protocols', () =>
        axios
          .get(`/data-api/partner/protocols/of-job/${this.order.job.sfid}`)
          .then((response) => {
            this.protocols = response.data.reduce(
              (acc, curr) => {
                if (
                  ['protocol-customer', 'protocol', 'cancellation'].includes(
                    curr.resource,
                  )
                ) {
                  acc.protocol.push(curr);
                }
                if (
                  [
                    JobMediaFileTypeEnum.PROPAGATED_PROTOCOL,
                    JobMediaFileTypeEnum.CUSTOMER_BLUEPRINT,
                  ].includes(curr.resource)
                ) {
                  acc.orderDetails.push(curr);
                }
                return acc;
              },
              {
                orderDetails: [],
                protocol: [],
              },
            );
          }),
      );
    },
    fetchPartnerBriefingDocuments() {
      // Orders without an opportunity legitimately have no briefing docs — skip
      // rather than fetch a doomed URL (which would falsely look like an error).
      if (!this.order.opportunity) return undefined;
      return this.runWithRetry('partnerBriefing', () =>
        axios
          .get(
            `/data-api/partner/partner-briefing-documents/${this.order.opportunity.sfid}/${this.order.api_id}`,
          )
          .then(({ data }) => {
            this.partnerBriefingDocuments = data;
          }),
      );
    },
    retryFormLoad() {
      FORM_LOAD_KEYS.forEach((key) => {
        const state = this.netRetry[key];
        if (state && state.hadError && state.status !== 'loaded') {
          this.retryNow(key);
        }
      });
    },
    validateUrl(url) {
      if (!url) return null;
      // Try original URL first
      try {
        return new URL(url).href;
      } catch {
        // Try with https:// prefix
        if (!url.match(/^[a-zA-Z]+:\/\//)) {
          try {
            return new URL('https://' + url).href;
          } catch {
            return null;
          }
        }
        return null;
      }
    },
    formattedMultiValue(rawValue) {
      const firstPass = parseMultiValueField(rawValue).map((v) => String(v));

      const expanded = firstPass.flatMap((value) => {
        const cleaned = value
          .replace(/\\\\/g, '\\')
          .replace(/\\"/g, '"')
          .trim();

        if (
          (cleaned.startsWith('[') && cleaned.endsWith(']')) ||
          (cleaned.startsWith('{') && cleaned.endsWith('}'))
        ) {
          return parseMultiValueField(cleaned);
        }
        return [cleaned];
      });

      const normalized = expanded
        .map((value) =>
          String(value)
            .replace(/\\/g, '')
            .replace(/^['"[{]+/, '')
            .replace(/['"]+$/, '')
            .replace(/[}\]]+$/, '')
            .trim(),
        )
        .filter((value) => value.length > 0);

      return normalized.join(', ');
    },
    handleTechnicalSupportCall(contact) {
      const callLink = `tel:${contact.phone}`;
      window.open(callLink, '_blank');

      this.contactSupport(contact);
      this.showTechnicalSupportModal = false;
    },
  },
  created() {
    this.fetchProtocols();
    this.fetchFormAnswers();
    this.fetchPartnerBriefingDocuments();

    axios
      .get('/data-api/sf-picklist', {
        params: {
          api_names: 'job_media_file_types',
          values_only: true,
        },
      })
      .then((response) => {
        this.jmfPicklist =
          response.data.job_media_file_types?.Metadata.customValue.sort(
            (a, b) => a.label.localeCompare(b.label),
          ) || [];
      });
  },
};
</script>
<style lang="scss" scoped>
.form-load-error {
  &__icon {
    font-size: 2.5rem;
    color: #adb5bd;
  }
}

.gid-product-name {
  max-width: 400px;
  font-size: 14px;
}

.gid-onhold-badge {
  font-size: 15px;
  font-weight: 700;
}

.btn-unreachable {
  background: none;
  border: none;
  color: var(--primary);
  padding: 0;
  transition: all 0.12s ease;

  &:hover {
    text-decoration: underline;
    color: #025c4c;
  }
}

@keyframes flash {
  0% {
    background-color: #f8d7da;
  }
  50% {
    background-color: #ffc107;
  }
}

.new-pdf-available {
  position: relative;
  padding-right: 30px;
}

.new-pdf-available::after {
  content: 'NEU';
  color: black;
  font-size: 12px;
  position: absolute;
  top: 1px;
  right: 11px;
  padding: 4px;
  border-radius: 5px;
  background-color: #f8d7da;
  animation: flash 4s infinite alternate;
}

.up-down {
  animation: up-down-animation 2.5s linear infinite;
}

@keyframes up-down-animation {
  0%,
  100% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(3px);
  }
  75% {
    transform: translateY(-3px);
  }
}

/* Stop the animation on hover */
.up-down:hover {
  animation: none;
}

.gid-product-hints {
  gap: 10px;
  display: flex;
  flex-direction: column;
  p {
    margin: 0;
  }
}

.gid-product-files {
  .product-file-item {
    .badge {
      font-size: 10px;
      padding: 2px 4px;
    }
  }
}
</style>
